import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const BookmarksContext = createContext(null);

export const BookmarksProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || user.subscription_status !== 'active') {
      setIds(new Set());
      setLoaded(true);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/bookmarks/ids`);
      setIds(new Set(data.sanction_ids || []));
    } catch (err) {
      // 401/403 or network — leave empty set
      setIds(new Set());
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(async (sanctionId) => {
    const isBookmarked = ids.has(sanctionId);
    // Optimistic update
    setIds((prev) => {
      const next = new Set(prev);
      if (isBookmarked) next.delete(sanctionId);
      else next.add(sanctionId);
      return next;
    });
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/api/bookmarks/${sanctionId}`);
      } else {
        await axios.post(`${API}/api/bookmarks/${sanctionId}`);
      }
    } catch (err) {
      // Revert on failure
      setIds((prev) => {
        const next = new Set(prev);
        if (isBookmarked) next.add(sanctionId);
        else next.delete(sanctionId);
        return next;
      });
    }
  }, [ids]);

  const isBookmarked = useCallback((sanctionId) => ids.has(sanctionId), [ids]);

  return (
    <BookmarksContext.Provider value={{ ids, isBookmarked, toggle, refresh, loaded }}>
      {children}
    </BookmarksContext.Provider>
  );
};

export const useBookmarks = () => {
  const ctx = useContext(BookmarksContext);
  if (!ctx) throw new Error('useBookmarks must be used within BookmarksProvider');
  return ctx;
};
